import { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { SideModal } from './SideModal'
import {
  FiSearch, FiLoader, FiAlertCircle, FiDatabase, FiArrowLeft, FiChevronRight,
} from 'react-icons/fi'

// Consulta Draco (2026-07-20, ideia do dono a partir do caso Eliane Potrich:
// "consta pago SIMONE?"). Busca QUALQUER cliente no Oracle por código, CPF ou
// nome e mostra o financeiro dos DOIS lados — débitos em aberto E pagamentos
// recentes. O painel de Cobrança só enxerga quem está em atraso; quem quitou
// some de lá — aqui não. Backend: /api/cobranca/cliente-busca (novo) +
// dossier /api/cobranca/cliente/:codigo (o mesmo da seção Financeiro da
// sidebar), ambos via proxy fino do fin.

interface Match { codigo: string; nome: string; tipoLabel: string | null; situacao: string | null; cpf: string | null; cidade: string | null }
interface Detail {
  cliente: {
    codigo: string; nome: string; tipoLabel: string | null; situacao: string | null
    grupo: string | null; distrito: string | null; cidade: string | null
    cpf: string | null; telefoneFormatado: string | null; email: string | null
  }
  boletosAbertos: { documento: string; serie: string; vencimento: string | null; valor: number; saldo: number; diasAtraso: number }[]
  totalAberto: number
  pagamentos: { documento: string; serie: string; vencimento: string | null; ultPagamento: string | null; valor: number; valorPago: number; saldo: number; quitado: boolean }[]
  pagamentosTotal: number
}

const brl = (n: number | null | undefined) => (n || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const fmtData = (d: string | null) => { if (!d) return '—'; const [y, m, dd] = d.slice(0, 10).split('-'); return `${dd}/${m}/${y.slice(2)}` }

export function ConsultaDracoModal({ onClose }: { onClose: () => void }) {
  const { authenticatedFetch } = useAuth()
  const [q, setQ] = useState('')
  const [searching, setSearching] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [matches, setMatches] = useState<Match[] | null>(null)
  const [detail, setDetail] = useState<Detail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)

  const buscar = async () => {
    if (searching || q.trim().length < 3) return
    setSearching(true); setError(null); setDetail(null)
    try {
      const r = await authenticatedFetch(`/api/cobranca/cliente-busca?q=${encodeURIComponent(q.trim())}`)
      const d = await r.json()
      if (!r.ok || !d.success) throw new Error(d.error || 'Falha na busca')
      setMatches(d.matches)
      if (d.matches.length === 1) abrirDetalhe(d.matches[0].codigo)
    } catch (e: any) { setError(e.message); setMatches(null) }
    finally { setSearching(false) }
  }

  const abrirDetalhe = async (codigo: string) => {
    setDetailLoading(true); setError(null)
    try {
      const r = await authenticatedFetch(`/api/cobranca/cliente/${encodeURIComponent(codigo)}`)
      const d = await r.json()
      if (!r.ok) throw new Error(d.error || `Erro ${r.status}`)
      setDetail(d)
    } catch (e: any) { setError(e.message) }
    finally { setDetailLoading(false) }
  }

  const c = detail?.cliente

  return (
    <SideModal
      title="Consulta Draco"
      subtitle="financeiro de qualquer cliente, direto do Oracle"
      icon={FiDatabase}
      onClose={onClose}
      maxWidth="max-w-2xl"
    >
      {/* Busca — sempre visível no topo */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            value={q} onChange={e => setQ(e.target.value)} autoFocus
            onKeyDown={e => { if (e.key === 'Enter') buscar() }}
            placeholder="código, CPF ou nome do cliente…"
            title="Busca no Oracle DRACO por código Tupperware, CPF ou nome (mín. 3 caracteres)"
            className="w-full rounded-lg border border-border bg-background pl-9 pr-3 py-2 text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          />
        </div>
        <button onClick={buscar} disabled={searching || q.trim().length < 3}
          className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium px-3 py-2 hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed">
          {searching ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiSearch className="w-4 h-4" />} Buscar
        </button>
      </div>

      {error && (
        <div className="text-sm text-red-500 flex items-center gap-2"><FiAlertCircle className="w-4 h-4 shrink-0" />{error}</div>
      )}

      {/* Lista de resultados */}
      {!detail && !detailLoading && matches && (
        matches.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">Nenhum cliente encontrado no DRACO pra "{q.trim()}".</div>
        ) : (
          <div className="divide-y divide-border/60 rounded-lg border border-border">
            {matches.map(m => (
              <button key={m.codigo} onClick={() => abrirDetalhe(m.codigo)}
                title={`Abrir o financeiro de ${m.nome}`}
                className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-secondary/50 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-foreground truncate">{m.nome}</div>
                  <div className="text-[11px] text-muted-foreground tabular-nums">
                    #{m.codigo}{m.tipoLabel ? ` · ${m.tipoLabel}` : ''}{m.cidade ? ` · ${m.cidade}` : ''}{m.cpf ? ` · CPF ${m.cpf}` : ''}
                  </div>
                </div>
                <FiChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
              </button>
            ))}
          </div>
        )
      )}

      {detailLoading && (
        <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
          <FiLoader className="w-4 h-4 animate-spin" /> Consultando o Oracle…
        </div>
      )}

      {/* Detalhe financeiro */}
      {detail && c && !detailLoading && (
        <div className="space-y-3">
          {matches && matches.length > 1 && (
            <button onClick={() => setDetail(null)} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
              <FiArrowLeft className="w-3.5 h-3.5" /> Voltar aos resultados
            </button>
          )}

          {/* Cabeçalho do cliente */}
          <div className="rounded-lg border border-border p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-foreground truncate">{c.nome}</div>
                <div className="text-[11px] text-muted-foreground tabular-nums">
                  #{c.codigo}{c.tipoLabel ? ` · ${c.tipoLabel}` : ''}{c.cpf ? ` · CPF ${c.cpf}` : ''}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {[c.grupo, c.distrito, c.cidade].filter(Boolean).join(' · ') || '—'}
                </div>
              </div>
              <div className="text-right shrink-0">
                {c.telefoneFormatado && <div className="text-[11px] text-muted-foreground tabular-nums">{c.telefoneFormatado}</div>}
                {c.email && <div className="text-[11px] text-muted-foreground truncate max-w-[180px]">{c.email}</div>}
              </div>
            </div>
          </div>

          {/* Resumo */}
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-lg bg-red-500/5 border border-red-500/20 px-3 py-2">
              <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Em aberto</div>
              <div className="text-sm font-bold text-red-600 dark:text-red-400 tabular-nums">{brl(detail.totalAberto)}</div>
              <div className="text-[10px] text-muted-foreground">{detail.boletosAbertos.length} título(s)</div>
            </div>
            <div className="rounded-lg bg-emerald-500/5 border border-emerald-500/20 px-3 py-2">
              <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Pago (recente)</div>
              <div className="text-sm font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">{brl(detail.pagamentosTotal)}</div>
              <div className="text-[10px] text-muted-foreground">{detail.pagamentos.length} pagamento(s)</div>
            </div>
          </div>

          {/* Débitos em aberto */}
          <div>
            <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Débitos em aberto ({detail.boletosAbertos.length})</div>
            {detail.boletosAbertos.length === 0 ? (
              <div className="text-xs text-muted-foreground/70 italic py-1">Nenhum débito em aberto — nada pendente no DRACO.</div>
            ) : (
              <div className="rounded-lg border border-border divide-y divide-border/50">
                {detail.boletosAbertos.map(b => {
                  const venceu = b.diasAtraso > 0
                  const cls = !venceu ? 'text-amber-500' : b.diasAtraso <= 30 ? 'text-orange-500' : 'text-red-500'
                  return (
                    <div key={`${b.documento}-${b.serie}`} className="px-3 py-1.5 flex items-center gap-3 text-xs">
                      <span className="font-mono text-muted-foreground w-28 shrink-0 truncate">{b.documento}{b.serie ? `·${b.serie}` : ''}</span>
                      <span className="text-muted-foreground/80 w-16 shrink-0 tabular-nums">{fmtData(b.vencimento)}</span>
                      <span className={`w-16 shrink-0 font-semibold tabular-nums ${cls}`}>{venceu ? `${b.diasAtraso}d atraso` : 'a vencer'}</span>
                      <span className="flex-1" />
                      <span className="font-semibold text-red-500 tabular-nums">{brl(b.saldo)}</span>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Pagamentos recentes — o lado que responde "consta pago?" */}
          <div>
            <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Pagamentos recentes ({detail.pagamentos.length})</div>
            {detail.pagamentos.length === 0 ? (
              <div className="text-xs text-muted-foreground/70 italic py-1">Nenhum pagamento registrado no período recente.</div>
            ) : (
              <div className="rounded-lg border border-border divide-y divide-border/50">
                {detail.pagamentos.map(p => (
                  <div key={`${p.documento}-${p.serie}`} className="px-3 py-1.5 flex items-center gap-3 text-xs">
                    <span className="font-mono text-muted-foreground w-28 shrink-0 truncate">{p.documento}{p.serie ? `·${p.serie}` : ''}</span>
                    <span className="text-muted-foreground/80 shrink-0 tabular-nums">pago {fmtData(p.ultPagamento)}</span>
                    <span className={`shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${p.quitado ? 'bg-emerald-500/15 text-emerald-600' : 'bg-amber-500/15 text-amber-600'}`}>{p.quitado ? 'Quitado' : 'Parcial'}</span>
                    <span className="flex-1" />
                    <span className="font-semibold text-emerald-600 tabular-nums">{brl(p.valorPago)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </SideModal>
  )
}
